'use client'

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import {
  useStmCreateCapacityException,
  useStmUpdateCapacityException,
} from '@/features/stm/hooks/use-stm-groups-blocks'
import { useStmWeeksWithStats } from '@/features/stm/hooks/use-stm-weeks'
import { formatDateRange, doesOverlap } from '@/features/stm/lib/date-utils'
import type { CapacityException } from '@/features/stm/lib/types'
import { Settings2 } from 'lucide-react'

const formSchema = z
  .object({
    start_date: z.string().min(1, 'Start date is required'),
    end_date: z.string().min(1, 'End date is required'),
    capacity: z.coerce.number().int().min(0, 'Capacity must be 0 or greater'),
    reason: z.string().max(200).optional(),
  })
  .refine((d) => d.start_date <= d.end_date, {
    message: 'End date must be after start date',
    path: ['end_date'],
  })

type FormValues = z.infer<typeof formSchema>

interface CapacityExceptionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  exception?: CapacityException | null
  defaultStartDate?: string
  defaultEndDate?: string
  regionId?: number | undefined | null
}

export function CapacityExceptionFormDialog({
  open,
  onOpenChange,
  exception,
  defaultStartDate,
  defaultEndDate,
  regionId,
}: CapacityExceptionFormDialogProps) {
  const createException = useStmCreateCapacityException(regionId ?? null)
  const updateException = useStmUpdateCapacityException()
  const { data: weeks } = useStmWeeksWithStats(regionId ?? null)
  const isEditing = !!exception

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      start_date: defaultStartDate || '',
      end_date: defaultEndDate || '',
      capacity: 20,
      reason: '',
    },
  })

  const watchedStart = form.watch('start_date')
  const watchedEnd = form.watch('end_date')
  const affectedWeeks = (weeks || []).filter(
    (w) => watchedStart && watchedEnd && doesOverlap(watchedStart, watchedEnd, w.week_start, w.week_end)
  )

  useEffect(() => {
    if (exception) {
      form.reset({
        start_date: exception.start_date,
        end_date: exception.end_date,
        capacity: exception.capacity,
        reason: exception.reason ?? '',
      })
    } else {
      form.reset({
        start_date: defaultStartDate || '',
        end_date: defaultEndDate || '',
        capacity: 20,
        reason: '',
      })
    }
  }, [exception, defaultStartDate, defaultEndDate, form, open])

  const onSubmit = async (values: FormValues) => {
    const data = {
      start_date: values.start_date,
      end_date: values.end_date,
      capacity: values.capacity,
      reason: values.reason?.trim() || null,
      severity: null as 'Hard Block' | 'Soft Block' | null,
    }
    if (isEditing) {
      await updateException.mutateAsync({ id: exception.id, ...data })
    } else {
      await createException.mutateAsync(data)
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            {isEditing ? 'Edit Capacity Exception' : 'Add Capacity Exception'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="capacity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Capacity *</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} {...field} />
                  </FormControl>
                  <FormDescription>
                    Override the default capacity for this date range
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Holiday week, reduced staffing" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {affectedWeeks.length > 0 && (
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="text-sm font-medium mb-2">Affected Weeks ({affectedWeeks.length})</p>
                <div className="flex flex-wrap gap-1">
                  {affectedWeeks.slice(0, 6).map((w) => (
                    <span key={w.id} className="text-xs px-2 py-1 bg-secondary rounded-full">
                      {formatDateRange(w.week_start, w.week_end)}
                    </span>
                  ))}
                  {affectedWeeks.length > 6 && (
                    <span className="text-xs px-2 py-1 text-muted-foreground">
                      +{affectedWeeks.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createException.isPending || updateException.isPending}
              >
                {isEditing ? 'Save Changes' : 'Add Capacity Exception'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
